import React, { useState, useEffect } from 'react';
import { Building } from '../types/building';
import { BuildingDataService } from '../services/buildingDataService';
import { BuildingAnalysisService } from '../services/buildingAnalysisService';
import { NonCompliantBuilding, ComplianceFilters, ComplianceSortOptions } from '../types/compliance';
import { RetrofitCategory } from '../types/retrofit';
import { 
    AlertTriangle, 
    Search, 
    Filter, 
    Download, 
    Phone, 
    Mail, 
    Building as BuildingIcon, 
    MapPin, 
    DollarSign,
    Calendar,
    TrendingUp,
    FileText,
    Upload
} from 'lucide-react';

interface NonCompliantBuildingsDirectoryProps {
    onBack: () => void;
}

const NonCompliantBuildingsDirectory: React.FC<NonCompliantBuildingsDirectoryProps> = ({ onBack }) => {
    const [buildings, setBuildings] = useState<NonCompliantBuilding[]>([]);
    const [filteredBuildings, setFilteredBuildings] = useState<NonCompliantBuilding[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [buildingTypes, setBuildingTypes] = useState<string[]>([]);
    const [boroughs, setBoroughs] = useState<string[]>([]);
    
    const [filters, setFilters] = useState<ComplianceFilters>({});
    const [sortOptions, setSortOptions] = useState<ComplianceSortOptions>({
        field: 'complianceScore',
        direction: 'asc'
    });

    useEffect(() => {
        loadDefaultData();
    }, []);

    useEffect(() => {
        applyFiltersAndSort();
    }, [buildings, searchTerm, filters, sortOptions]);

    const loadDefaultData = async () => {
        setIsLoading(true);
        try {
            // Generate mock data for demo
            const mockBuildings = generateMockData();
            setBuildings(mockBuildings);
        } catch (error) {
            console.error('Error loading default data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setIsLoading(true);
            try {
                await processFile(file);
            } catch (error) {
                console.error('Error processing file:', error);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const processFile = async (file: File) => {
        const allBuildings = await BuildingDataService.parseCSVFile(file);
        
        // Get non-compliant buildings
        const nonCompliant = await BuildingAnalysisService.getNonCompliantBuildings(allBuildings);
        setBuildings(nonCompliant);
        
        // Generate mock data for demo purposes if no non-compliant buildings found
        if (nonCompliant.length === 0) {
            const mockBuildings = generateMockData(allBuildings.slice(0, 20));
            setBuildings(mockBuildings);
        }
        
        // Update filter options
        allBuildings.forEach((building: Building) => {
            if (building.buildingType && !buildingTypes.includes(building.buildingType)) {
                setBuildingTypes(prev => [...prev, building.buildingType!].sort());
            }
            if (building.borough && !boroughs.includes(building.borough)) {
                setBoroughs(prev => [...prev, building.borough!].sort());
            }
        });
    };

    const generateMockData = (baseBuildings?: Building[]): NonCompliantBuilding[] => {
        const mockData: NonCompliantBuilding[] = [];
        const sampleBuildings = baseBuildings || [
            {
                id: '1',
                propertyName: 'Manhattan Office Tower',
                address: '123 Broadway',
                borough: 'Manhattan',
                buildingType: 'office',
                grossFloorArea: 150000,
                totalGHGEmissions: 2500,
                yearBuilt: 1985
            },
            {
                id: '2',
                propertyName: 'Brooklyn Residential Complex',
                address: '456 Atlantic Ave',
                borough: 'Brooklyn',
                buildingType: 'residential',
                grossFloorArea: 85000,
                totalGHGEmissions: 1200,
                yearBuilt: 1975
            }
        ] as Building[];

        sampleBuildings.forEach((building, index) => {
            const complianceData = BuildingAnalysisService.analyzeCompliance(building);
            if (!complianceData.isCompliant) {
                const retrofitRecommendations = [
                    {
                        id: `hvac-${building.id}`,
                        title: 'HVAC System Upgrade',
                        description: 'Replace aging HVAC equipment with high-efficiency systems',
                        category: RetrofitCategory.HVAC,
                        priority: 'high' as const,
                        estimatedCost: 150000 + (index * 25000),
                        estimatedSavings: 25000 + (index * 5000),
                        paybackPeriod: 6,
                        energyReduction: 20,
                        emissionReduction: 18,
                        implementation: {
                            phases: ['Energy audit', 'System design', 'Installation', 'Commissioning'],
                            timeline: '6-8 months',
                            requirements: ['DOB permits', 'Professional engineer certification']
                        }
                    }
                ];

                mockData.push({
                    ...building,
                    complianceData,
                    retrofitRecommendations,
                    priority: index % 4 === 0 ? 'urgent' : index % 3 === 0 ? 'high' : index % 2 === 0 ? 'medium' : 'low',
                    estimatedCost: 200000 + (index * 50000),
                    potentialSavings: 35000 + (index * 8000),
                    paybackPeriod: 5.5 + (index * 0.5)
                });
            }
        });

        return mockData;
    };

    const applyFiltersAndSort = () => {
        let result = [...buildings];

        // Apply search filter
        if (searchTerm) {
            result = result.filter(building =>
                building.propertyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                building.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                building.borough?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Apply filters
        result = BuildingAnalysisService.filterBuildings(result, filters);

        // Apply sorting
        result = BuildingAnalysisService.sortBuildings(result, sortOptions);

        setFilteredBuildings(result);
    };

    const handleFilterChange = (key: keyof ComplianceFilters, value: any) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const exportToCSV = () => {
        const headers = [
            'Property Name',
            'Address',
            'Borough',
            'Building Type',
            'Gross Floor Area',
            'Compliance Score',
            'Priority',
            'Estimated Cost',
            'Potential Savings',
            'Payback Period',
            'Violations',
            'Next Deadline'
        ];

        const csvData = filteredBuildings.map(building => [
            building.propertyName || '',
            building.address || '',
            building.borough || '',
            building.buildingType || '',
            building.grossFloorArea?.toString() || '',
            building.complianceData.complianceScore.toString(),
            building.priority,
            building.estimatedCost.toString(),
            building.potentialSavings.toString(),
            building.paybackPeriod.toString(),
            building.complianceData.violations.length.toString(),
            building.complianceData.nextDeadline || ''
        ]);

        const csvContent = [headers, ...csvData]
            .map(row => row.map(cell => `"${cell}"`).join(','))
            .join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `non-compliant-buildings-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const getUniqueValues = (key: keyof Building) => {
        return Array.from(new Set(buildings.map(b => b[key] as string))).filter(Boolean).sort();
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading non-compliant buildings...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white shadow-sm border-b">
                <div className="max-w-7xl mx-auto px-6 py-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <AlertTriangle className="h-8 w-8 text-red-600" />
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900">
                                    Non-Compliant Buildings Directory
                                </h1>
                                <p className="text-gray-600 mt-1">
                                    Buildings failing to meet Local Law 97 requirements
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-4">
                            <input
                                type="file"
                                accept=".csv"
                                onChange={handleFileUpload}
                                className="hidden"
                                id="file-upload"
                            />
                            <label
                                htmlFor="file-upload"
                                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 cursor-pointer"
                            >
                                <Upload className="h-4 w-4" />
                                <span>Upload CSV</span>
                            </label>
                            <button
                                onClick={exportToCSV}
                                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
                                disabled={filteredBuildings.length === 0}
                            >
                                <Download className="h-4 w-4" />
                                <span>Export</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-6">
                {/* Summary Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                    <div className="bg-white rounded-lg shadow-sm p-6">
                        <div className="flex items-center">
                            <BuildingIcon className="h-8 w-8 text-red-600" />
                            <div className="ml-3">
                                <p className="text-sm font-medium text-gray-600">Total Buildings</p>
                                <p className="text-2xl font-bold text-gray-900">{filteredBuildings.length}</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow-sm p-6">
                        <div className="flex items-center">
                            <DollarSign className="h-8 w-8 text-yellow-600" />
                            <div className="ml-3">
                                <p className="text-sm font-medium text-gray-600">Total Est. Cost</p>
                                <p className="text-2xl font-bold text-gray-900">
                                    ${(filteredBuildings.reduce((sum, b) => sum + b.estimatedCost, 0) / 1000000).toFixed(1)}M
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow-sm p-6">
                        <div className="flex items-center">
                            <TrendingUp className="h-8 w-8 text-green-600" />
                            <div className="ml-3">
                                <p className="text-sm font-medium text-gray-600">Potential Savings</p>
                                <p className="text-2xl font-bold text-gray-900">
                                    ${(filteredBuildings.reduce((sum, b) => sum + b.potentialSavings, 0) / 1000000).toFixed(1)}M
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow-sm p-6">
                        <div className="flex items-center">
                            <AlertTriangle className="h-8 w-8 text-orange-600" />
                            <div className="ml-3">
                                <p className="text-sm font-medium text-gray-600">Urgent Priority</p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {filteredBuildings.filter(b => b.priority === 'urgent').length}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Search and Filters */}
                <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                        <div className="flex-1 max-w-md">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search buildings..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>
                        <div className="flex items-center space-x-3">
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${
                                    showFilters ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                <Filter className="h-4 w-4" />
                                <span>Filters</span>
                            </button>
                            <select
                                value={`${sortOptions.field}-${sortOptions.direction}`}
                                onChange={(e) => {
                                    const [field, direction] = e.target.value.split('-') as [any, 'asc' | 'desc'];
                                    setSortOptions({ field, direction });
                                }}
                                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="complianceScore-asc">Compliance Score (Low to High)</option>
                                <option value="complianceScore-desc">Compliance Score (High to Low)</option>
                                <option value="estimatedCost-desc">Cost (High to Low)</option>
                                <option value="estimatedCost-asc">Cost (Low to High)</option>
                                <option value="potentialSavings-desc">Savings (High to Low)</option>
                                <option value="paybackPeriod-asc">Payback Period (Short to Long)</option>
                            </select>
                        </div>
                    </div>

                    {/* Advanced Filters */}
                    {showFilters && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-200">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Building Type</label>
                                <select
                                    value={filters.buildingType || ''}
                                    onChange={(e) => handleFilterChange('buildingType', e.target.value || undefined)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">All Types</option>
                                    {getUniqueValues('buildingType').map(type => (
                                        <option key={type} value={type}>{type}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Borough</label>
                                <select
                                    value={filters.borough || ''}
                                    onChange={(e) => handleFilterChange('borough', e.target.value || undefined)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">All Boroughs</option>
                                    {getUniqueValues('borough').map(borough => (
                                        <option key={borough} value={borough}>{borough}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                                <select
                                    value={filters.priority || ''}
                                    onChange={(e) => handleFilterChange('priority', e.target.value || undefined)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">All Priorities</option>
                                    <option value="urgent">Urgent</option>
                                    <option value="high">High</option>
                                    <option value="medium">Medium</option>
                                    <option value="low">Low</option>
                                </select>
                            </div>
                        </div>
                    )}
                </div>

                {/* Buildings List */}
                <div className="space-y-6">
                    {filteredBuildings.map((building) => (
                        <div key={building.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                            <div className="p-6">
                                {/* Building Header */}
                                <div className="flex justify-between items-start mb-4">
                                    <div className="flex-1">
                                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                            {building.propertyName || 'Unnamed Property'}
                                        </h3>
                                        <div className="flex items-center text-gray-600 text-sm mb-2">
                                            <MapPin className="h-4 w-4 mr-1" />
                                            <span>{building.address}, {building.borough}</span>
                                        </div>
                                        <div className="flex items-center space-x-4">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                building.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                                                building.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                                                building.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                                'bg-green-100 text-green-800'
                                            }`}>
                                                {building.priority.toUpperCase()} PRIORITY
                                            </span>
                                            <span className="text-sm text-gray-600">
                                                Compliance Score: {building.complianceData.complianceScore}/100
                                            </span>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="mb-2">
                                            <p className="text-sm text-gray-600">Estimated Retrofit Cost</p>
                                            <p className="text-2xl font-bold text-red-600">
                                                ${(building.estimatedCost / 1000).toFixed(0)}K
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-600">Annual Savings</p>
                                            <p className="text-lg font-semibold text-green-600">
                                                ${(building.potentialSavings / 1000).toFixed(0)}K
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Compliance Issues */}
                                <div className="mb-6">
                                    <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                                        <AlertTriangle className="h-4 w-4 mr-2 text-red-600" />
                                        Compliance Issues ({building.complianceData.violations.length})
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {building.complianceData.violations.slice(0, 2).map((violation, index) => (
                                            <div key={index} className="bg-red-50 border border-red-200 rounded p-3">
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className={`text-xs font-medium px-2 py-1 rounded ${
                                                        violation.severity === 'high' ? 'bg-red-100 text-red-800' :
                                                        violation.severity === 'medium' ? 'bg-orange-100 text-orange-800' :
                                                        'bg-yellow-100 text-yellow-800'
                                                    }`}>
                                                        {violation.severity.toUpperCase()}
                                                    </span>
                                                    <span className="text-xs text-gray-600 capitalize">
                                                        {violation.type}
                                                    </span>
                                                </div>
                                                <p className="text-sm text-gray-800">{violation.description}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Empty State */}
                {filteredBuildings.length === 0 && !isLoading && (
                    <div className="text-center py-12">
                        <AlertTriangle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No buildings found</h3>
                        <p className="text-gray-600 mb-4">
                            {buildings.length === 0 
                                ? 'Upload a CSV file to analyze building compliance data'
                                : 'Try adjusting your search criteria or filters'
                            }
                        </p>
                        {buildings.length === 0 && (
                            <label
                                htmlFor="file-upload"
                                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer"
                            >
                                <Upload className="h-4 w-4 mr-2" />
                                Upload Building Data
                            </label>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default NonCompliantBuildingsDirectory;
